// App -> Deals -> apiService.js
import axios from 'axios';
import { API_URL } from '../../../config';

// Configure the base API route
const api = axios.create({
  baseURL: `${API_URL}`,
  withCredentials: true,
});

// ======================
// SINGLE source for filters (+cache)
// ======================

// Simple module-level cache: TTL and request deduplication
let filtersCache = {
  data: null,
  etag: null,
  expiresAt: 0,
  inFlight: null, // Promise
};

// TTL can be adjusted if needed (in ms)
const FILTERS_TTL = 60_000; // 1 minute

async function getFilters() {
  const now = Date.now();

  // 1) valid cache
  if (filtersCache.data && now < filtersCache.expiresAt) {
    return filtersCache.data;
  }

  // 2) request already in flight: wait for the results
  if (filtersCache.inFlight) {
    return filtersCache.inFlight;
  }

  // 3) new request
  filtersCache.inFlight = (async () => {
    try {
      const headers = {};
      if (filtersCache.etag) headers['If-None-Match'] = filtersCache.etag;

      const resp = await api.get('/deals-info/filters', { headers, validateStatus: () => true });

      if (resp.status === 304 && filtersCache.data) {
        filtersCache.expiresAt = now + FILTERS_TTL;
        return filtersCache.data;
      }

      if (resp.status >= 200 && resp.status < 300) {
        const data = resp.data ?? {};

        const rounds = Array.isArray(data.rounds) ? data.rounds.filter(Boolean) : [];
        const amount = data.amount ?? {};
        const dates  = data.dates ?? {};
        const industry = data.industry ?? {};
        const industryCategories = Array.isArray(industry.categories) ? industry.categories.filter(Boolean) : [];
        // tags are not needed on the frontend yet

        const normalized = {
          rounds,
          amount: {
            min: typeof amount.min === 'number' ? amount.min : null,
            max: typeof amount.max === 'number' ? amount.max : null,
          },
          dates: {
            min: dates.min || null,
            max: dates.max || null,
          },
          industry: {
            categories: industryCategories,
          },
        };

        filtersCache.data = normalized;
        filtersCache.etag = resp.headers?.etag || filtersCache.etag || null;
        filtersCache.expiresAt = Date.now() + FILTERS_TTL;
        return normalized;
      }

      const msg = resp.data?.message || `Failed to fetch filters (status ${resp.status})`;
      throw new Error(msg);
    } finally {
      filtersCache.inFlight = null;
    }
  })();

  return filtersCache.inFlight;
}

// Export for the category list (similar to rounds)
export const fetchIndustryCategories = async () => {
  const filters = await getFilters();
  return filters.industry?.categories ?? [];
};


// ======================
// Existing functions (API compatibility for components)
// ======================

// Function to get the list of unique rounds (as before: string[])
export const fetchInvestmentRounds = async () => {
  const filters = await getFilters();
  // old contract: just an array
  return filters.rounds;
};

export const fetchInvestmentAmountRange = async () => {
  const filters = await getFilters();
  return {
    min_am_usd: filters.amount.min ?? undefined,
    max_am_usd: filters.amount.max ?? undefined,
  };
};

// Function to get the date range (as before: { min_date, max_date })
export const fetchDateRange = async () => {
  const filters = await getFilters();
  return {
    min_date: filters.dates.min ?? undefined,
    max_date: filters.dates.max ?? undefined,
  };
};

// ======================
// Everything else unchanged
// ======================

// Function to get the deals list with server-side pagination
export const fetchDeals = async (page, limit, filters, sortOrder) => {
  try {
    const params = {
      page,
      limit,
      ...(filters.selectedRounds?.length > 0 && {
        rounds: filters.selectedRounds.map((round) => round.trim()).join(','),
      }),
      ...(filters.investmentAmountFrom != null && {
        investmentAmountFrom: filters.investmentAmountFrom,
      }),
      ...(filters.investmentAmountTo != null && {
        investmentAmountTo: filters.investmentAmountTo,
      }),
      ...(filters.keyword && { keyword: filters.keyword }),
      ...(filters.fromDate && { fromDate: filters.fromDate.toISOString() }),
      ...(filters.toDate && { toDate: filters.toDate.toISOString() }),

      // ✅ NEW: industry categories
      ...(filters.industryCategories?.length > 0 && {
        industryCategories: filters.industryCategories.join(','),
      }),

      sortOrder,
    };

    const response = await axios.get(`${API_URL}/deals`, {
      params,
      withCredentials: true,
    });

    const { deals, totalCount } = response.data;
    return { data: deals, totalCount };
  } catch (error) {
    console.error('Ошибка при получении сделок:', error);
    throw error;
  }
};
